import { useEffect } from "preact/hooks";

import * as compose from "../lib/compose";
import { Compose } from "./Compose";
import { Overlays } from "./Overlays";

/**
 * A window holding one message being written, and nothing else.
 *
 * The composer's counterpart to `ui/MessageWindow.tsx`, and the same shape for
 * the same reasons: no sidebar, no list, no tab strip, because this is not a
 * second copy of the client. The window was opened at the composer's address,
 * so `app.tsx` has already asked `lib/composeWindow.ts` to fill it in from that
 * address; this only decides what is drawn around it.
 *
 * # The same composer, in a different container
 *
 * `<Compose />` is the component the browser draws as a layer over the mail,
 * used here verbatim. That is what makes a compose window read as the composer
 * having moved rather than as a second thing that also writes mail -- and a
 * second implementation would be a second place for the two builds, and now the
 * two window kinds, to stop agreeing about what a half-written message looks
 * like.
 *
 * Its Close button needs no special case. `compose.closeAfterSaving` saves the
 * draft and then puts the composer away, and putting it away in a window means
 * closing the window -- see `leaveWindow` in `lib/compose.ts`, which is where
 * that decision lives so that every route out of the composer takes it,
 * including a finished send and a discarded draft.
 *
 * # Why the overlays are here
 *
 * The same reason `ui/MessageWindow.tsx` has them, learned the same way: the
 * keyboard handler is global and does not care which window it is in, so `?`
 * and the outbox strip and every toast raised in here need somewhere to render
 * or they set a signal nothing is listening to. The outbox strip in particular
 * is not decoration -- this window hands a message over and then closes, and
 * the strip is what carries "accepted, not sent" afterwards. It happens to
 * carry it in the *main* window, which is where somebody will be looking.
 */
export function ComposeWindow() {
  // What the window switcher calls this. Rust cannot know it -- the subject is
  // being typed on this side of the webview boundary, and for a reply it is
  // derived rather than copied -- so the window opens under a placeholder and
  // this replaces it, and keeps replacing it as the subject changes. Four
  // windows all called "New message" is a switcher that has said nothing.
  const subject = compose.draft.value.subject.trim();
  useEffect(() => {
    document.title = subject || "New message";
  }, [subject]);

  return (
    <>
      {/*
        The composer, in a container that gives it the whole window rather than
        floating it over something. This window wears the native OS titlebar
        (Amit's call, 2026-09-21), so it does not draw HydraMail's own
        WindowControls -- the OS provides move, minimise and close, and the
        title comes from `document.title` above. The stylesheet still hides
        `.compose-head`: its title and Close would only duplicate the
        titlebar's. Dragging lives on the field and footer areas (the
        `data-tauri-drag-region` attributes in `ui/Compose.tsx`).
      */}
      <div class="solo compose-solo">
        <Compose />
      </div>
      {/* Everything floating except the composer itself -- the window is the
          composer, and `composeOpen` is inevitably true in here. */}
      <Overlays composer={false} />
    </>
  );
}

/**
 * What this window shows when it could not be filled in.
 *
 * A compose window is loaded at an address naming a message -- the parent of a
 * reply, the draft being resumed -- and fetching it can fail: the daemon is
 * down, the draft was deleted from another device, the session expired between
 * the click and the load. There is nothing to fall back to here the way there
 * is in the main window, where a failed composer leaves the mail on screen.
 *
 * So it says so, plainly, rather than opening a blank composer. A blank
 * composer where a reply was asked for is the silent wrong answer this whole
 * feature's grammar is written strictly to avoid, and it would be worst here:
 * the recipients, the quoting and the threading headers would all be missing
 * from something that looks ready to send.
 */
export function ComposeWindowFailed({ why }: { why: string }) {
  return (
    <>
      <div class="solo compose-solo">
        <div class="empty">
          <p>This message could not be opened.</p>
          <p class="dim">{why}</p>
          <p class="dim">
            A draft is still in the Drafts folder.
          </p>
        </div>
      </div>
      {/* Toasts, and nothing that assumes a composer. */}
      <Overlays composer={false} />
    </>
  );
}
